import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [needMfa, setNeedMfa] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, needMfa ? mfaCode : undefined);
      toast.success("Access granted. Welcome to Pandora Nexus.");
      navigate("/");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Authentication failed";
      if (detail === "MFA code required") {
        setNeedMfa(true);
        toast.message("Enter the 6-digit code from your authenticator app");
      } else if (detail === "Invalid MFA code") {
        toast.error(detail);
        setMfaCode("");
      } else {
        toast.error(detail);
        setNeedMfa(false);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page" style={{ background: "var(--bg-app)" }}>
      {/* Left visual */}
      <div className="hidden lg:flex flex-1 relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "url('https://static.prod-images.emergentagent.com/jobs/4b9f3037-ad4c-4c58-8d8a-97b64a5806c3/images/47048d77dcedaa496035a29088019f5f89c0761fec19ff439a51d0c75751f72f.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0" style={{ background: "rgba(5, 11, 20, 0.78)" }} />
        <div className="absolute inset-0 nexus-topo opacity-30" />

        <div className="relative z-10 flex flex-col justify-between p-14 w-full">
          <div className="flex items-center gap-3">
            <img src="/pandora-nexus-mark.png" alt="Pandora Nexus" className="w-12 h-12 object-contain"
                 style={{ filter: "drop-shadow(0 0 14px rgba(212,175,55,0.4))" }} />
            <div>
              <div className="font-display text-xl font-semibold tracking-tight text-white">
                Pandora<span style={{ color: "var(--gold-500)" }}>Nexus</span>
              </div>
              <div className="label-eyebrow mt-1">Institutional Operating System</div>
            </div>
          </div>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <div className="label-eyebrow mb-4">Authorized Access // Pandora Management Consult Ltd</div>
            <h1 className="font-display text-5xl xl:text-6xl font-medium text-white leading-tight tracking-tight">
              Africa's AI-powered
              <br />
              <span style={{ color: "var(--gold-500)" }}>institutional intelligence</span>
              <br />
              & operational ecosystem.
            </h1>
            <p className="mt-6 text-slate-400 max-w-md leading-relaxed">
              Executive consulting, government engagement, investor relations, strategic research, workflow automation — unified
              in one institutional-grade platform.
            </p>
          </motion.div>

          <div className="flex items-center gap-8 text-[11px] font-mono tracking-widest text-slate-500 uppercase">
            <span>SOC-2 Aligned</span>
            <span className="w-1 h-1 rounded-full bg-slate-700" />
            <span>AES-256 At-Rest</span>
            <span className="w-1 h-1 rounded-full bg-slate-700" />
            <span>Audit Trails</span>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center justify-center mb-10">
            <img src="/pandora-nexus-logo.png" alt="Pandora Nexus" className="h-20 object-contain"
                 style={{ filter: "drop-shadow(0 0 20px rgba(212,175,55,0.3))" }} />
          </div>

          {/* Logo also visible above the form on large screens as a focus anchor */}
          <div className="hidden lg:flex justify-center mb-8">
            <img src="/pandora-nexus-mark.png" alt="" aria-hidden="true" className="h-14 object-contain opacity-90"
                 style={{ filter: "drop-shadow(0 0 16px rgba(212,175,55,0.35))" }} />
          </div>

          <div className="label-eyebrow mb-2">// Secure Authentication</div>
          <h2 className="font-display text-3xl font-medium text-white tracking-tight mb-2">Access the Nexus</h2>
          <p className="text-slate-400 text-sm mb-10">
            Sign in with your institutional credentials to enter the command center.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" strokeWidth={1.5} />
                <input
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-[#0A111F] border border-white/10 text-white font-mono text-sm placeholder:text-slate-600 focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40 transition-all"
                  placeholder="you@institution.com"
                />
              </div>
            </div>

            <div>
              <label className="label-eyebrow block mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" strokeWidth={1.5} />
                <input
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-[#0A111F] border border-white/10 text-white font-mono text-sm focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40 transition-all"
                  placeholder="••••••••••••"
                />
              </div>
            </div>

            {needMfa && (
              <div data-testid="login-mfa-block">
                <label className="label-eyebrow block mb-2">Authenticator Code</label>
                <input
                  data-testid="login-mfa-input"
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="w-full px-4 py-3 bg-[#0A111F] border border-[#D4AF37]/40 text-white font-mono text-2xl tracking-widest text-center focus:border-[#D4AF37] focus:outline-none focus:ring-1 focus:ring-[#D4AF37]/40 transition-all"
                  placeholder="000000"
                />
                <p className="text-[11px] text-slate-500 mt-2 font-mono tracking-wide">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>
            )}

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-3 mt-4 font-medium tracking-wide transition-all disabled:opacity-60"
              style={{ background: "var(--gold-500)", color: "#050B14" }}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  {needMfa ? "Verify & Initiate Session" : "Initiate Session"} <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 flex items-center justify-center gap-6 text-[10px] font-mono tracking-widest text-slate-500">
            <Link to="/about" data-testid="login-about-link" className="hover:text-[#D4AF37] transition-colors">ABOUT PANDORA NEXUS</Link>
            <span className="text-slate-700">·</span>
            <Link to="/pricing" data-testid="login-pricing-link" className="hover:text-[#D4AF37] transition-colors">PRICING</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
